'use client';

/**
 * ObeyaProjectListCard — mode1's explorer surface (cards-everywhere).
 *
 * Mode1-owned copy of the legacy `<ProjectsExplorer />`. Same data
 * source (obeya.list + project_explore), same tree-with-filter
 * shape. Typography rides the experimental "B" variant
 * `lib/design/tokens.x.ts` (vs tokens.ts = "A" / mode4 baseline): names
 * take the `treeDepth` ramp, secondary text the em `text.*` styles.
 * Seeded at parity with the baseline; tweak tokens.x to A/B the projects
 * surface against products. Cohabitation: legacy `projects-explorer.tsx`
 * stays in place for non-mode1 obeya surfaces; the two copies retire
 * together when legacy obeya does.
 *
 * @see klyde_edge/composition/cards/obeya.py — content_key="obeya_project_list"
 *
 * @component_role: Card
 * @component_type: Explorer Card
 * @domain: Explorer / Obeya (mode1)
 * @layer: presentation
 */

import { useEffect, useState, useMemo } from 'react';
import { ChevronRight, FileText, Package, DollarSign, Lightbulb, Loader2, MoreVertical, Pencil, Trash2, Download, ClipboardCopy, Link2, Check, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { useProjects, useProjectSubfolders, useMcpRfqLink, useMcpRfqUnlink } from '@/lib/api/hooks';
import { useTabStore } from '@/lib/stores/tab-store';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { useLayoutStore } from '@/lib/stores/layout-store';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useWorkspaceLabels, useAffordance } from '@/lib/stores/total-matrix-store';
import { useLanguageStore } from '@/lib/stores/language-store';
import { useMcpActions } from '@/lib/hooks/use-mcp-actions';
import { useRfqActions } from '@/lib/hooks/use-rfq-actions';
import { cn } from '@/lib/utils/cn';
import { text, treeDepth, surfaceBase } from '@/lib/design/tokens.x';
import { CardPlacementProvider, useCardExport, downloadCapture, waitForRailSelection } from '@/lib/design/card-export';
import type { ProjectSummary, ProjectExploreData, McpSummary, ProductWishSummary } from '@/lib/api/types';
import { testIds } from '@/lib/test-utils/selectors';

type ChildItem = ProjectExploreData['rfqs'][number] | ProjectExploreData['pos'][number];

export function ObeyaProjectListCard() {
  const projectsStrategy = useCapabilityStore((s) => s.strategyName('obeya', 'list'));
  const { data, isLoading, error } = useProjects(projectsStrategy!, { limit: 50 });
  const [stateFilter, setStateFilter] = useState<string>('all');

  // The project roster query backs every project card's identity — shared
  // across the per-project cards so the envelope dedups to one payload.
  // Matches useProjects' key shape (['projects', strategy, params]).
  const rosterQueryKey = useMemo(
    () => ['projects', projectsStrategy ?? '', { limit: 50 }] as const,
    [projectsStrategy],
  );
  // Layer-1 labels ride with the obeya.list response (data.labels), the same
  // lane portfolio-card reads — not hardcoded TSX. Threaded to the kebab.
  const labels = data?.labels ?? {};

  // Collect unique states from all loaded project children
  // (derived from data, not from a separate API call)
  const allStates = useMemo(() => {
    if (!data?.projects) return [];
    // States will populate as projects expand; static list covers common Odoo states
    return ['draft', 'sent', 'purchase', 'done', 'cancel'];
  }, [data]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-4">
        <Loader2 className="w-4 h-4 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn('p-4 text-destructive', text.body)}>
        Failed to load projects. Check connection.
      </div>
    );
  }

  if (!data?.projects.length) {
    return (
      <div className={cn('p-4', text.muted)}>
        No projects found.
      </div>
    );
  }

  return (
    <div data-testid={testIds.explorer.projectList} className="py-1" style={{ fontSize: surfaceBase.canvas }}>
      {/* Status filter bar */}
      <div className="flex flex-wrap gap-1 px-2 pb-2">
        <FilterChip label="All" active={stateFilter === 'all'} onClick={() => setStateFilter('all')} />
        {allStates.map((s) => (
          <FilterChip key={s} label={s} active={stateFilter === s} onClick={() => setStateFilter(s)} />
        ))}
      </div>

      <div className="space-y-1.5 px-2">
        {data.projects.map((project) => (
          // Each project is a card, generated from the roster. Per-instance
          // slot_id keeps the same-kind cards distinct in the envelope.
          <CardPlacementProvider
            key={project.semantic_id}
            value={{ surface: 'explorer', slot_id: `project:${project.semantic_id}` }}
          >
            <ProjectTreeItem project={project} stateFilter={stateFilter} rosterQueryKey={rosterQueryKey} labels={labels} />
          </CardPlacementProvider>
        ))}
      </div>
    </div>
  );
}

function FilterChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'px-2 py-0.5 text-xs rounded-full border transition-colors capitalize',
        active
          ? 'bg-primary text-primary-foreground border-primary'
          : 'bg-transparent text-muted-foreground border-border hover:border-foreground/30'
      )}
    >
      {label}
    </button>
  );
}

function ProjectTreeItem({ project, stateFilter, rosterQueryKey, labels }: { project: ProjectSummary; stateFilter: string; rosterQueryKey: readonly unknown[]; labels: Record<string, string> }) {
  const [expanded, setExpanded] = useState(false);
  const hasChildren = project.rfq_count > 0 || project.po_count > 0;
  const openTab = useTabStore((state) => state.openTab);
  const selectNode = useGraphSelectionStore((state) => state.selectNode);
  const setProjectContext = useGraphSelectionStore((state) => state.setProjectContext);
  const selectedProjectId = useGraphSelectionStore((state) => state.projectId);
  const queryClient = useQueryClient();
  const activeExplorerView = useLayoutStore((s) => s.activeExplorerView);
  const lang = useLanguageStore((s) => s.lang);
  // Role-gated kebab affordances (mirrors mode4 vendor): capture is
  // admin-only, copy unrestricted. undefined = not in projection = hidden.
  const copyTestId = useAffordance('explorer.project.copy_report');
  const downloadTestId = useAffordance('explorer.project.download_capture');
  // Dynamic — every roster row renders simultaneously, so the open
  // button is addressed by this row's own semantic_id (params={id}),
  // not a static key. Supersedes the old ad-hoc `testIds.explorer
  // .projectItem` helper — one catalog, not two parallel systems.
  const openTestId = useAffordance('explorer.project.open', { id: project.semantic_id });
  const [menuOpen, setMenuOpen] = useState(false);

  // Auto-expand when this project becomes the active selection from
  // anywhere — explorer click, canvas portfolio-card click, or graph
  // node click all route through graph-selection-store.projectId.
  // Expand-on-match, never collapse-on-mismatch (preserves rows the
  // user expanded manually). Mirrors the legacy obeya-projects-list
  // -section behaviour so mode1 canvas → explorer stays in sync.
  useEffect(() => {
    if (selectedProjectId === project.semantic_id && hasChildren) {
      setExpanded(true);
    }
  }, [selectedProjectId, project.semantic_id, hasChildren]);

  // V2: data gateway strategy call — lightweight children for sub-folders
  const exploreStrategy = useCapabilityStore((s) => s.strategyName('project_intelligence', 'explore'));
  const { data: response, isLoading } = useProjectSubfolders(
    exploreStrategy!,
    expanded ? project.semantic_id : null
  );

  const exploreData = response?.data as ProjectExploreData | undefined;

  // Apply state filter to RFQs and POs (MCPs have no state — always shown).
  // Tenders come pre-grouped from the BFF; the UI-only state filter trims
  // each tender's sibling list and drops tenders with no remaining matches.
  // No grouping logic here — BFF owns the shape.
  const filteredTenders = useMemo(() => {
    const tenders = exploreData?.tenders ?? [];
    if (stateFilter === 'all') return tenders;
    return tenders
      .map((t) => ({
        ...t,
        rfqs: t.rfqs.filter((r) => r.state === stateFilter),
      }))
      .filter((t) => t.rfqs.length > 0);
  }, [exploreData?.tenders, stateFilter]);
  const totalRfqCount = useMemo(
    () => filteredTenders.reduce((n, t) => n + t.rfqs.length, 0),
    [filteredTenders],
  );

  const filteredPos = useMemo(() => {
    if (!exploreData?.pos) return [];
    if (stateFilter === 'all') return exploreData.pos;
    return exploreData.pos.filter((p) => p.state === stateFilter);
  }, [exploreData?.pos, stateFilter]);

  const mcps = exploreData?.mcps ?? [];
  const wishes = exploreData?.product_wishes ?? [];

  const handleOpenProject = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Order matters: openTab() calls clearSelection() internally
    // (tab-store.ts:59), which nulls graph-store.projectId. So openTab
    // MUST run before setProjectContext — otherwise the clear wipes the
    // projectId the expand-on-match effect above depends on. (Canvas
    // Timeline/Graph cards read projectId via useViewportSelection,
    // which also falls back to the project tab's entityId.)
    // ProjectSummary doesn't carry RAG fields (the legacy obeya project
    // type had them); the mode1 cards consume projectId only, so the
    // RAG strings default to empty.
    openTab({
      id: `project-${project.semantic_id}`,
      type: 'project',
      title: project.name,
      entityId: project.semantic_id,
    });
    setProjectContext(project.semantic_id, {
      name: project.name,
      rag_status: project.status ?? '',
      rag_label: '',
      rfq_count: project.rfq_count,
      po_count: project.po_count,
    });
  };

  const handleToggleExpand = () => {
    if (hasChildren) {
      setExpanded(!expanded);
    }
  };

  // Scene export — this project as a card (collection-card child of the
  // explorer root). Children (RFQs / POs / Cost Plans / Wishes) ride in
  // json.tree when expanded; they graduate to their own cards in the next
  // iteration. status is 'loaded' — the project identity is always present
  // (it's from the roster); the lazy children are a sub-detail.
  const exportValue = useMemo(() => ({
    label: project.name,
    content_key: 'project',
    status: 'loaded' as const,
    query_key: rosterQueryKey,
    text: `${project.name} — ${project.rfq_count} RFQ · ${project.po_count} PO`,
    markdown: [`### ${project.name}`, `${project.rfq_count} RFQ · ${project.po_count} PO`].join('\n'),
    json: {
      project_id: project.semantic_id,
      name: project.name,
      status: project.status ?? null,
      rfq_count: project.rfq_count,
      po_count: project.po_count,
      tree:
        expanded && exploreData
          ? {
              rfqs: filteredTenders.flatMap((t) =>
                t.rfqs.map((r) => ({ rfq_id: r.semantic_id, name: r.name, state: r.state })),
              ),
              pos: filteredPos.map((p) => ({ po_id: p.semantic_id, name: p.name, state: p.state })),
              mcps: mcps.map((m) => ({ mcp_id: m.semantic_id, name: m.name })),
              wishes: wishes.map((w) => ({ wish_id: w.semantic_id, name: w.name, category: w.category })),
            }
          : null,
    },
    state: { expanded, active: selectedProjectId === project.semantic_id },
  }), [project, expanded, exploreData, filteredTenders, filteredPos, mcps, wishes, rosterQueryKey, selectedProjectId]);
  useCardExport(exportValue);

  // Copy this project — reuse the registered markdown so clipboard == export.
  const handleCopy = async () => {
    setMenuOpen(false);
    try {
      await navigator.clipboard.writeText(exportValue.markdown || exportValue.text);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[ProjectTreeItem] copy failed:', err);
    }
  };

  // Select-then-capture: if this project isn't active, open it (paints the
  // canvas preset) and wait for the selection to settle before capturing.
  // Strategy-only download; the error is diagnostic. Admin-gated via the
  // affordance; capture_scene re-enforces at the auth boundary regardless.
  const handleDownloadCapture = async () => {
    setMenuOpen(false);
    if (selectedProjectId !== project.semantic_id) {
      openTab({
        id: `project-${project.semantic_id}`,
        type: 'project',
        title: project.name,
        entityId: project.semantic_id,
      });
      setProjectContext(project.semantic_id, {
        name: project.name,
        rag_status: project.status ?? '',
        rag_label: '',
        rfq_count: project.rfq_count,
        po_count: project.po_count,
      });
      await waitForRailSelection(project.semantic_id, 5000);
    }
    try {
      await downloadCapture(queryClient, {
        mode: activeExplorerView ?? null,
        selection: project.semantic_id,
        resolved_selection: project.semantic_id,
        selection_source: 'explicit',
        viewer_lang: lang ?? 'en',
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[ProjectTreeItem] capture failed:', err);
    }
  };

  return (
    <div
      className={cn(
        'relative rounded-lg border bg-card transition-colors',
        selectedProjectId === project.semantic_id
          ? 'border-primary/30 bg-primary/5'
          : 'border-border hover:bg-accent/30',
      )}
    >
      {/* Project Row */}
      <div
        className={cn(
          'group w-full flex items-center gap-1 px-2 py-1.5 text-sm transition-colors',
          !hasChildren && 'pl-6'
        )}
      >
        {hasChildren && (
          <button onClick={handleToggleExpand}>
            <ChevronRight
              className={cn(
                'w-4 h-4 shrink-0 transition-transform',
                expanded && 'rotate-90'
              )}
            />
          </button>
        )}
        <button
          data-testid={openTestId}
          onClick={handleOpenProject}
          className="flex items-center gap-1 flex-1 min-w-0 text-left"
        >
          <span className={cn(treeDepth[0], 'truncate flex-1')}>{project.name}</span>
        </button>
        {(project.rfq_count > 0 || project.po_count > 0) && (
          <span className={cn(text.muted, 'shrink-0')}>
            {project.rfq_count > 0 && `${project.rfq_count} RFQ`}
            {project.rfq_count > 0 && project.po_count > 0 && ', '}
            {project.po_count > 0 && `${project.po_count} PO`}
          </span>
        )}
        {/* Kebab — per-project capture/copy. Present only when this actor's
            role projection carries at least one action (capture is admin-only;
            copy is unrestricted). */}
        {(copyTestId || downloadTestId) && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen((v) => !v);
            }}
            aria-label={labels.project_menu_label ?? 'Project actions'}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            className="p-1 rounded shrink-0 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors opacity-0 group-hover:opacity-100 aria-expanded:opacity-100"
          >
            <MoreVertical className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {menuOpen && (copyTestId || downloadTestId) && (
        <>
          {/* Click-outside backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setMenuOpen(false)}
            aria-hidden="true"
          />
          <div
            role="menu"
            className="absolute right-1 top-10 z-50 w-52 rounded-md border border-border bg-popover shadow-md py-1 text-sm"
          >
            {copyTestId && (
              <button
                type="button"
                role="menuitem"
                data-testid={copyTestId}
                onClick={handleCopy}
                className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-accent text-left"
              >
                <ClipboardCopy className="w-3.5 h-3.5" /> {labels.action_copy_project ?? 'Copy'}
              </button>
            )}
            {downloadTestId && (
              <button
                type="button"
                role="menuitem"
                data-testid={downloadTestId}
                onClick={() => void handleDownloadCapture()}
                className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-accent text-left"
              >
                <Download className="w-3.5 h-3.5" /> {labels.action_download_capture ?? 'Download capture'}
              </button>
            )}
          </div>
        </>
      )}

      {/* Expanded Children — sub-folder sections */}
      {expanded && (
        <div className="ml-4 border-l border-border/50">
          {isLoading ? (
            <div className="flex items-center gap-2 px-4 py-1 text-sm text-muted-foreground">
              <Loader2 className="w-3 h-3 animate-spin" />
              Loading...
            </div>
          ) : (
            <>
              {totalRfqCount > 0 && (
                <ChildSection label="RFQs" icon={FileText} iconColor="text-yellow-400" count={totalRfqCount}>
                  <RfqTenderList
                    tenders={filteredTenders}
                    projectSid={project.semantic_id}
                    selectNode={selectNode}
                    mcps={mcps}
                  />
                </ChildSection>
              )}

              {filteredPos.length > 0 && (
                <ChildSection label="Purchase Orders" icon={Package} iconColor="text-green-400" count={filteredPos.length}>
                  {filteredPos.map((po) => (
                    <CardPlacementProvider key={po.semantic_id} value={{ surface: 'explorer', slot_id: `po:${po.semantic_id || ''}` }}>
                      <ChildRow
                        item={po}
                        type="po"
                        icon={Package}
                        iconColor="text-green-400"
                        testId={testIds.explorer.orderItem(po.semantic_id || '')}
                        projectSid={project.semantic_id}
                        selectNode={selectNode}
                      />
                    </CardPlacementProvider>
                  ))}
                </ChildSection>
              )}

              {mcps.length > 0 && (
                <ChildSection label="Cost Plans" icon={DollarSign} iconColor="text-amber-400" count={mcps.length}>
                  {mcps.map((mcp) => (
                    <CardPlacementProvider key={mcp.semantic_id} value={{ surface: 'explorer', slot_id: `mcp:${mcp.semantic_id}` }}>
                      <McpRow mcp={mcp} projectSid={project.semantic_id} selectNode={selectNode} />
                    </CardPlacementProvider>
                  ))}
                </ChildSection>
              )}

              {wishes.length > 0 && (
                <ChildSection label="Product Wishes" icon={Lightbulb} iconColor="text-orange-400" count={wishes.length}>
                  {wishes.map((wish) => (
                    <CardPlacementProvider key={wish.semantic_id} value={{ surface: 'explorer', slot_id: `product_wish:${wish.semantic_id}` }}>
                      <WishRow wish={wish} projectSid={project.semantic_id} selectNode={selectNode} />
                    </CardPlacementProvider>
                  ))}
                </ChildSection>
              )}

            </>
          )}
        </div>
      )}
    </div>
  );
}

/** Collapsible section header with count badge. */
function ChildSection({
  label,
  icon: Icon,
  iconColor,
  count,
  children,
}: {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  iconColor: string;
  count: number;
  children: React.ReactNode;
}) {
  // Open by default — when a project row expands, its RFQ / PO / Cost
  // Plan / Wish sections show their rows straight away (one level, not
  // two). Matches the legacy obeya-projects-list-section ChildSection.
  const [open, setOpen] = useState(true);

  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-1 px-2 py-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
      >
        <ChevronRight className={cn('w-3 h-3 shrink-0 transition-transform', open && 'rotate-90')} />
        <Icon className={cn('w-3 h-3 shrink-0', iconColor)} />
        <span className={cn(treeDepth[1], 'font-medium')}>{label}</span>
        <span className={cn(text.muted, 'ml-auto')}>{count}</span>
      </button>
      {open && <div>{children}</div>}
    </div>
  );
}

/** Single RFQ or PO child row — click to view, kebab for Edit / Download. */
function ChildRow({
  item,
  type,
  icon: Icon,
  iconColor,
  testId,
  projectSid,
  selectNode,
  labelOverride,
  mcps,
}: {
  item: ChildItem;
  type: 'rfq' | 'po';
  icon: React.ComponentType<{ className?: string }>;
  iconColor: string;
  testId?: string;
  projectSid: string;
  selectNode: (node: { id: string; label: string; type: string; status?: string; properties: Record<string, unknown> }, projectId: string) => void;
  /** Override the row's display label (e.g. vendor name inside a TenderGroup,
   *  where the tender name lives on the parent header). */
  labelOverride?: string | null;
  mcps?: McpSummary[];
}) {
  const selectedNode = useGraphSelectionStore((state) => state.selectedNode);
  const openTab = useTabStore((state) => state.openTab);
  const { editRfq, downloadRfq, downloadRfqTypst, cancelRfq } = useRfqActions();
  const [menuOpen, setMenuOpen] = useState(false);
  const [linkSubmenuOpen, setLinkSubmenuOpen] = useState(false);
  const rfqLinkStrategy = useCapabilityStore((s) => s.strategyName('cost_plans', 'rfq_link'));
  const rfqUnlinkStrategy = useCapabilityStore((s) => s.strategyName('cost_plans', 'rfq_unlink'));
  const rfqLink = useMcpRfqLink(rfqLinkStrategy!);
  const rfqUnlink = useMcpRfqUnlink(rfqUnlinkStrategy!);
  const { state: stateLabels } = useWorkspaceLabels();
  const stateDisplay = item.state ? (stateLabels[item.state] ?? item.state) : '';

  // Only RFQs carry linked_plan_sid/name (see ProjectExploreData['rfqs']).
  const linkedPlanSid = type === 'rfq' ? (item as ProjectExploreData['rfqs'][number]).linked_plan_sid : null;
  const linkedPlanName = type === 'rfq' ? (item as ProjectExploreData['rfqs'][number]).linked_plan_name : null;

  const handleLinkToPlan = (plan: McpSummary) => {
    setMenuOpen(false);
    setLinkSubmenuOpen(false);
    rfqLink.mutate(
      { project_sid: projectSid, plan_sid: plan.semantic_id, rfq_sid: item.semantic_id || '' },
      {
        onSuccess: () => toast.success(`Linked to ${plan.name}`),
        onError: (err) => toast.error('Link failed', { description: err.message }),
      },
    );
  };

  const handleUnlink = () => {
    setMenuOpen(false);
    setLinkSubmenuOpen(false);
    if (!linkedPlanSid) return;
    rfqUnlink.mutate(
      { project_sid: projectSid, plan_sid: linkedPlanSid, rfq_sid: item.semantic_id || '' },
      {
        onSuccess: () => toast.success('Unlinked'),
        onError: (err) => toast.error('Unlink failed', { description: err.message }),
      },
    );
  };

  // Scene export — RFQ / PO as a card (collection-card child). Scene-only
  // (query_key: null): the data rides in the project's lazy subfolder fetch
  // (the payload-bearing collection level), so this card paints + captures
  // without claiming its own payload.
  useCardExport(useMemo(() => ({
    label: labelOverride ?? item.name ?? type.toUpperCase(),
    content_key: type,
    status: 'loaded' as const,
    query_key: null,
    text: `${labelOverride ?? item.name ?? ''}${stateDisplay ? ` (${stateDisplay})` : ''}`,
    json: { id: item.semantic_id, name: item.name, type, state: item.state ?? null },
    state: { selected: selectedNode?.id === item.semantic_id },
  }), [item, type, labelOverride, stateDisplay, selectedNode]));

  // RFQ / PO row click flips the canvas axis to this entity by
  // opening a tab. The cards-canvas-widget keys off `strategySelection`
  // (= activeTab.type), so without an openTab call the canvas stays
  // locked on the project's preset list and never sees the RFQ-scoped
  // presets (Matrix v2 etc.). selectNode then refreshes the rail.
  const handleClick = () => {
    openTab({
      id: `${type}-${item.semantic_id}`,
      type,
      title: item.name || type.toUpperCase(),
      entityId: item.semantic_id || '',
    });
    selectNode({
      id: item.semantic_id || '',
      label: item.name || type.toUpperCase(),
      type,
      status: item.state || undefined,
      properties: item as unknown as Record<string, unknown>,
    }, projectSid);
  };

  return (
    <div
      data-testid={testId}
      className={cn(
        'group relative w-full flex items-center gap-1 px-2 py-1 text-sm hover:bg-accent/50 pl-6',
        selectedNode?.id === item.semantic_id && 'bg-accent'
      )}
    >
      <button
        type="button"
        onClick={handleClick}
        className="flex items-center gap-1 flex-1 min-w-0 text-left"
      >
        <Icon className={cn('w-4 h-4 shrink-0', iconColor)} />
        <span className={cn(treeDepth[2], 'truncate flex-1')}>{labelOverride ?? item.name}</span>
        {linkedPlanName && (
          <span
            title={`Linked to ${linkedPlanName}`}
            className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 text-xs shrink-0 max-w-20"
          >
            <Link2 className="w-3 h-3 shrink-0" />
            <span className="truncate">{linkedPlanName}</span>
          </span>
        )}
        <span className={text.muted}>{stateDisplay}</span>
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setMenuOpen((v) => !v);
        }}
        aria-label="RFQ actions"
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted"
      >
        <MoreVertical className="w-3.5 h-3.5" />
      </button>
      {menuOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setMenuOpen(false)}
            aria-hidden="true"
          />
          <div
            role="menu"
            className="absolute right-1 top-7 z-50 w-48 rounded-md border border-border bg-popover shadow-md py-1 text-sm"
          >
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setMenuOpen(false);
                void editRfq(item.semantic_id || '');
              }}
              className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-accent text-left"
            >
              <Pencil className="w-3.5 h-3.5" /> Edit
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setMenuOpen(false);
                void downloadRfq(item.semantic_id || '', item.name);
              }}
              className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-accent text-left"
            >
              <Download className="w-3.5 h-3.5" /> Download
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setMenuOpen(false);
                void downloadRfqTypst(item.semantic_id || '', item.name);
              }}
              className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-accent text-left"
            >
              <Download className="w-3.5 h-3.5" /> Download (Typst)
            </button>
            {type === 'rfq' && mcps && mcps.length > 0 && (
              <div className="relative">
                <button
                  type="button"
                  role="menuitem"
                  onClick={(e) => {
                    e.stopPropagation();
                    setLinkSubmenuOpen((v) => !v);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-accent text-left truncate"
                >
                  <Link2 className="w-3.5 h-3.5 shrink-0" />
                  <span className="truncate">{linkedPlanName ? `Linked to ${linkedPlanName}` : 'Link to Cost Plan…'}</span>
                </button>
                {linkSubmenuOpen && (
                  <div
                    role="menu"
                    className="absolute right-full top-0 mr-1 w-44 rounded-md border border-border bg-popover shadow-md py-1 text-sm"
                  >
                    {mcps.map((plan) => {
                      const isCurrent = plan.semantic_id === linkedPlanSid;
                      return (
                        <button
                          key={plan.semantic_id}
                          type="button"
                          role="menuitem"
                          disabled={rfqLink.isPending || rfqUnlink.isPending}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (isCurrent) handleUnlink();
                            else handleLinkToPlan(plan);
                          }}
                          className={cn(
                            'w-full flex items-center gap-2 px-3 py-1.5 hover:bg-accent text-left truncate disabled:opacity-50',
                            isCurrent && 'text-emerald-400',
                          )}
                        >
                          {isCurrent ? <Check className="w-3.5 h-3.5 shrink-0" /> : <span className="w-3.5 shrink-0" />}
                          <span className="truncate">{plan.name}</span>
                        </button>
                      );
                    })}
                    {linkedPlanSid && (
                      <button
                        type="button"
                        role="menuitem"
                        disabled={rfqUnlink.isPending}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleUnlink();
                        }}
                        className="w-full flex items-center gap-2 px-3 py-1.5 text-destructive hover:bg-destructive/10 text-left border-t border-border mt-1 pt-1.5 disabled:opacity-50"
                      >
                        Unlink
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setMenuOpen(false);
                void cancelRfq(item.semantic_id || '', item.name);
              }}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-destructive hover:bg-destructive/10 text-left"
            >
              <Trash2 className="w-3.5 h-3.5" /> Cancel
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/** Single MCP (CostPlan) child row — click to view, kebab for Edit / Delete. */
function McpRow({
  mcp,
  projectSid,
  selectNode,
}: {
  mcp: McpSummary;
  projectSid: string;
  selectNode: (node: { id: string; label: string; type: string; properties: Record<string, unknown> }, projectId: string) => void;
}) {
  const selectedNode = useGraphSelectionStore((state) => state.selectedNode);
  const setMcpContext = useGraphSelectionStore((state) => state.setMcpContext);
  const openTab = useTabStore((state) => state.openTab);
  const { editMcp, deleteMcp, aiProfileMcp, downloadMcp, downloadMcpSchedule, downloadMcpScheduleTypst } = useMcpActions();
  const [menuOpen, setMenuOpen] = useState(false);
  const openTestId = useAffordance('explorer.mcp.open', { id: mcp.semantic_id });
  const actionsMenuTestId = useAffordance('explorer.mcp.actions_menu', { id: mcp.semantic_id });
  const editTestId = useAffordance('explorer.mcp.edit');
  const aiProfileTestId = useAffordance('explorer.mcp.ai_profile');

  // Scene export — Cost Plan as a card (collection-card child, scene-only).
  useCardExport(useMemo(() => ({
    label: mcp.name || 'Cost Plan',
    content_key: 'mcp',
    status: 'loaded' as const,
    query_key: null,
    text: mcp.name || 'Cost Plan',
    json: { id: mcp.semantic_id, name: mcp.name },
    state: { selected: selectedNode?.id === mcp.semantic_id },
  }), [mcp, selectedNode]));

  return (
    <div
      className={cn(
        'group relative w-full flex items-center gap-1 px-2 py-1 text-sm hover:bg-accent/50 pl-6',
        selectedNode?.id === mcp.semantic_id && 'bg-accent',
      )}
    >
      <button
        type="button"
        data-testid={openTestId}
        onClick={() => {
          // Same reason RFQ/PO rows need this (see that handler's comment
          // above): the cards-canvas-widget keys off `strategySelection`
          // (= activeTab.type). Without openTab, selectNode alone only
          // refreshes the rail — the canvas stays on whatever preset was
          // already active and never shows mode1_costplan/obeya_costplan.
          openTab({
            id: `mcp-${mcp.semantic_id}`,
            type: 'mcp',
            title: mcp.name || 'Cost Plan',
            entityId: mcp.semantic_id,
          });
          // Anchors the cost-plan graph card (costplan-graph-card.tsx) — must
          // run after openTab, which calls clearSelection() and would wipe
          // this otherwise. Same ordering rule as setProjectContext above.
          setMcpContext(mcp.semantic_id, mcp.name || 'Cost Plan');
          selectNode({
            id: mcp.semantic_id,
            label: mcp.name || 'Cost Plan',
            type: 'mcp',
            properties: mcp as unknown as Record<string, unknown>,
          }, projectSid);
        }}
        className="flex items-center gap-1 flex-1 min-w-0 text-left"
      >
        <DollarSign className="w-4 h-4 shrink-0 text-amber-400" />
        <span className={cn(treeDepth[2], 'truncate flex-1')}>{mcp.name}</span>
      </button>
      <button
        type="button"
        data-testid={actionsMenuTestId}
        onClick={(e) => {
          e.stopPropagation();
          setMenuOpen((v) => !v);
        }}
        aria-label="Cost plan actions"
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted"
      >
        <MoreVertical className="w-3.5 h-3.5" />
      </button>
      {menuOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setMenuOpen(false)}
            aria-hidden="true"
          />
          <div
            role="menu"
            className="absolute right-1 top-7 z-50 w-36 rounded-md border border-border bg-popover shadow-md py-1 text-sm"
          >
            <button
              type="button"
              role="menuitem"
              data-testid={editTestId}
              onClick={() => {
                setMenuOpen(false);
                void editMcp(mcp.semantic_id);
              }}
              className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-accent text-left"
            >
              <Pencil className="w-3.5 h-3.5" /> Edit
            </button>
            <button
              type="button"
              role="menuitem"
              data-testid={aiProfileTestId}
              onClick={() => {
                setMenuOpen(false);
                void aiProfileMcp(mcp.semantic_id);
              }}
              className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-accent text-left"
            >
              <Sparkles className="w-3.5 h-3.5" /> Klyde AI Profile
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setMenuOpen(false);
                void downloadMcp(mcp.semantic_id, mcp.name);
              }}
              className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-accent text-left"
            >
              <DollarSign className="w-3.5 h-3.5" /> Cost Plan
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setMenuOpen(false);
                void downloadMcpSchedule(mcp.semantic_id, mcp.name);
              }}
              className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-accent text-left"
            >
              <FileText className="w-3.5 h-3.5" /> Project Schedule
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setMenuOpen(false);
                void downloadMcpScheduleTypst(mcp.semantic_id, mcp.name);
              }}
              className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-accent text-left"
            >
              <FileText className="w-3.5 h-3.5" /> Schedule (Typst)
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setMenuOpen(false);
                void deleteMcp(mcp.semantic_id, mcp.name);
              }}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-destructive hover:bg-destructive/10 text-left"
            >
              <Trash2 className="w-3.5 h-3.5" /> Delete
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/** Single buyer product wish row. */
function WishRow({
  wish,
  projectSid,
  selectNode,
}: {
  wish: ProductWishSummary;
  projectSid: string;
  selectNode: (node: { id: string; label: string; type: string; properties: Record<string, unknown> }, projectId: string) => void;
}) {
  const selectedNode = useGraphSelectionStore((state) => state.selectedNode);

  // Scene export — Product Wish as a card (collection-card child, scene-only).
  useCardExport(useMemo(() => ({
    label: wish.name || 'Product Wish',
    content_key: 'product_wish',
    status: 'loaded' as const,
    query_key: null,
    text: wish.name || 'Product Wish',
    json: { id: wish.semantic_id, name: wish.name, category: wish.category ?? null },
    state: { selected: selectedNode?.id === wish.semantic_id },
  }), [wish, selectedNode]));

  return (
    <button
      onClick={() => selectNode({
        id: wish.semantic_id,
        label: wish.name || 'Product Wish',
        type: 'product_wish',
        properties: wish as unknown as Record<string, unknown>,
      }, projectSid)}
      className={cn(
        'w-full flex items-center gap-1 px-2 py-1 text-sm text-left hover:bg-accent/50 pl-6',
        selectedNode?.id === wish.semantic_id && 'bg-accent'
      )}
    >
      <Lightbulb className="w-4 h-4 shrink-0 text-orange-400" />
      <span className={cn(treeDepth[2], 'truncate flex-1')}>{wish.name}</span>
      {wish.category && (
        <span className={cn(text.muted, 'capitalize')}>{wish.category}</span>
      )}
    </button>
  );
}

// ────────────────────────────────────────────────────────────────────
// Tender rendering — BFF ships the tender shape pre-grouped (server-
// decided: tender_id, tender_name, label, display_as_group). The
// frontend iterates and paints. No bucketing, no fallback labels, no
// single-vs-multi-vendor decision here.
//
// Clicking the tender header *both* expands the group *and* flips the
// canvas via `openTab({type:'rfq', entityId: first sibling's sid})`.
// The matrix BFF (`rfq_matrix`) fans out to all siblings sharing
// `x_rfq_group_id` server-side, so any sibling's sid loads the full
// tender's comparison.
// ────────────────────────────────────────────────────────────────────

function RfqTenderList({
  tenders,
  projectSid,
  selectNode,
  mcps,
}: {
  tenders: ProjectExploreData['tenders'];
  projectSid: string;
  selectNode: (node: { id: string; label: string; type: string; status?: string; properties: Record<string, unknown> }, projectId: string) => void;
  mcps: McpSummary[];
}) {
  return (
    <>
      {tenders.map((t, idx) => {
        const key = t.tender_id || t.rfqs[0]?.semantic_id || `t-${idx}`;
        if (!t.display_as_group) {
          // Single-vendor tender — flat row.
          return (
            <CardPlacementProvider key={key} value={{ surface: 'explorer', slot_id: `rfq:${t.rfqs[0]?.semantic_id || ''}` }}>
              <ChildRow
                item={t.rfqs[0]}
                type="rfq"
                icon={FileText}
                iconColor="text-yellow-400"
                testId={testIds.explorer.rfqItem(t.rfqs[0]?.semantic_id || '')}
                projectSid={projectSid}
                selectNode={selectNode}
                mcps={mcps}
              />
            </CardPlacementProvider>
          );
        }
        return (
          <TenderGroup
            key={key}
            label={t.label ?? 'Tender'}
            rfqs={t.rfqs}
            projectSid={projectSid}
            selectNode={selectNode}
            mcps={mcps}
          />
        );
      })}
    </>
  );
}

function TenderGroup({
  label,
  rfqs,
  projectSid,
  selectNode,
  mcps,
}: {
  label: string;
  rfqs: ProjectExploreData['rfqs'];
  projectSid: string;
  selectNode: (node: { id: string; label: string; type: string; status?: string; properties: Record<string, unknown> }, projectId: string) => void;
  mcps: McpSummary[];
}) {
  const [open, setOpen] = useState(false);
  const openTab = useTabStore((state) => state.openTab);

  // Tender header click — expand AND flip the canvas to the tender's
  // matrix. Use the first sibling's sid as the entity anchor; the BFF
  // fans out across siblings server-side.
  const anchor = rfqs[0];
  const handleHeaderClick = () => {
    setOpen((v) => !v);
    if (!anchor?.semantic_id) return;
    openTab({
      id: `rfq-${anchor.semantic_id}`,
      type: 'rfq',
      title: label,
      entityId: anchor.semantic_id,
    });
    selectNode({
      id: anchor.semantic_id,
      label,
      type: 'rfq',
      status: anchor.state || undefined,
      properties: anchor as unknown as Record<string, unknown>,
    }, projectSid);
  };

  return (
    <div>
      <button
        type="button"
        onClick={handleHeaderClick}
        className="w-full flex items-center gap-1 px-2 py-1 text-sm text-left hover:bg-accent/50 pl-6"
      >
        <ChevronRight
          className={cn(
            'w-3 h-3 shrink-0 transition-transform text-muted-foreground',
            open && 'rotate-90',
          )}
        />
        <FileText className="w-4 h-4 shrink-0 text-yellow-400" />
        <span className={cn(treeDepth[1], 'truncate flex-1 font-medium')}>{label}</span>
        <span className={text.muted}>{rfqs.length} vendors</span>
      </button>
      {open && (
        <div className="ml-3 border-l border-border/30">
          {rfqs.map((rfq) => (
            <CardPlacementProvider key={rfq.semantic_id} value={{ surface: 'explorer', slot_id: `rfq:${rfq.semantic_id || ''}` }}>
              <ChildRow
                item={rfq}
                type="rfq"
                icon={FileText}
                iconColor="text-yellow-400"
                testId={testIds.explorer.rfqItem(rfq.semantic_id || '')}
                projectSid={projectSid}
                selectNode={selectNode}
                labelOverride={rfq.vendor_name || rfq.name}
                mcps={mcps}
              />
            </CardPlacementProvider>
          ))}
        </div>
      )}
    </div>
  );
}

